import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

/**
 * How the header bar is built, as opposed to what is in it.
 *
 * The bar had colours, an arrangement and a logo, and nothing else — so every
 * brand's header was the same shape with different words in it. These are the
 * decisions that make one look like a shop and another like a magazine:
 * how far the contents sit from the edges, how much air is between the links,
 * whether the bar is solid or frosted over what scrolls beneath it.
 *
 * Grouped by what someone is trying to change rather than by which field it
 * writes — spacing together, type together, finish together.
 */

const DEFAULTS = {
  maxWidth: 1200, gutter: 24, height: 64, linkGap: 24, brandGap: 40,
  menuVariant: 'plain', fontSize: 15, uppercase: false, letterSpacing: 0, bold: false,
  glass: { enabled: false, blur: 12, amount: 60 },
  buttonStyle: 'solid', border: true, shadow: false,
};

const Row = ({ label, hint, value, unit = 'px', min, max, step = 1, onChange, disabled }) => (
  <Box>
    <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
      <Typography variant='caption' color='text.secondary'>{label}</Typography>
      <Typography variant='caption' color='text.secondary'>
        {value}{unit}
      </Typography>
    </Stack>
    <Slider
      size='small'
      min={min}
      max={max}
      step={step}
      value={Number(value)}
      onChange={(_e, v) => onChange(v)}
      valueLabelDisplay='auto'
      disabled={disabled}
    />
    {hint && (
      <Typography variant='caption' color='text.secondary' display='block' mt='-4px'>
        {hint}
      </Typography>
    )}
  </Box>
);

export default function HeaderStyleControls({ style, onChange, sticky, onStickyChange }) {
  const s = { ...DEFAULTS, ...(style || {}), glass: { ...DEFAULTS.glass, ...((style || {}).glass || {}) } };
  const set = (patch) => onChange({ ...s, ...patch });
  const setGlass = (patch) => onChange({ ...s, glass: { ...s.glass, ...patch } });

  return (
    <Stack spacing='16px'>
      <Box>
        <Typography variant='body2' fontWeight='700' mb='8px'>Space</Typography>
        <Stack spacing='10px'>
          <Row
            label='Distance from the sides'
            hint='How far the contents sit from the window edges.'
            value={s.gutter} min={0} max={120}
            onChange={(v) => set({ gutter: v })}
          />
          <Row
            label='Widest the contents get'
            hint={s.maxWidth === 0 ? 'Edge to edge.' : 'Centred once the window is wider than this.'}
            value={s.maxWidth} min={0} max={2000} step={20}
            onChange={(v) => set({ maxWidth: v })}
          />
          <Row label='Bar height' value={s.height} min={40} max={160} onChange={(v) => set({ height: v })} />
          <Row
            label='Between the links'
            value={s.linkGap} min={0} max={80}
            onChange={(v) => set({ linkGap: v })}
          />
          <Row
            label='Between the name and the menu'
            value={s.brandGap} min={0} max={160}
            onChange={(v) => set({ brandGap: v })}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant='body2' fontWeight='700' mb='8px'>Menu</Typography>
        <Stack spacing='10px'>
          <FormControl size='small' fullWidth>
            <InputLabel>Link style</InputLabel>
            <Select
              label='Link style'
              value={s.menuVariant}
              onChange={(e) => set({ menuVariant: e.target.value })}
            >
              <MenuItem value='plain'>Plain — just the words</MenuItem>
              <MenuItem value='underline'>Underline on the current one</MenuItem>
              <MenuItem value='pill'>Pill — a tinted rounded background</MenuItem>
              <MenuItem value='bordered'>Outlined — a box around each</MenuItem>
            </Select>
          </FormControl>

          <Row label='Text size' value={s.fontSize} min={11} max={24} onChange={(v) => set({ fontSize: v })} />
          <Row
            label='Letter spacing'
            value={s.letterSpacing} min={-1} max={4} step={0.5}
            onChange={(v) => set({ letterSpacing: v })}
          />

          <Stack direction='row' spacing='8px' flexWrap='wrap' useFlexGap>
            <FormControlLabel
              control={<Switch size='small' checked={s.bold} onChange={(e) => set({ bold: e.target.checked })} />}
              label={<Typography variant='body2'>Bold</Typography>}
            />
            <FormControlLabel
              control={<Switch size='small' checked={s.uppercase} onChange={(e) => set({ uppercase: e.target.checked })} />}
              label={<Typography variant='body2'>Capitals</Typography>}
            />
          </Stack>

          <Typography variant='caption' color='text.secondary'>
            A menu link with links under it opens a dropdown — add them beneath
            it in the menu below, as deep as you need.
          </Typography>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant='body2' fontWeight='700' mb='8px'>Finish</Typography>
        <Stack spacing='10px'>
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={s.glass.enabled}
                onChange={(e) => setGlass({ enabled: e.target.checked })}
              />
            }
            label={<Typography variant='body2'>Frosted glass</Typography>}
          />
          {s.glass.enabled ? (
            <>
              <Typography variant='caption' color='text.secondary'>
                The bar becomes translucent and blurs whatever scrolls under it.
              </Typography>
              {/* Glass has nothing to blur unless the bar stays put while the
                  page moves under it. Offered here rather than only mentioned,
                  because the setting lives in another section and "turn on
                  something over there" is how a feature ends up looking
                  broken. */}
              {!sticky && (
                <FormControlLabel
                  control={
                    <Switch
                      size='small'
                      checked={false}
                      onChange={() => onStickyChange?.(true)}
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      Keep the bar on screen while scrolling — without this there
                      is nothing for the glass to blur.
                    </Typography>
                  }
                />
              )}
              <Row
                label='How solid'
                hint='0 is clear glass, 100 is the plain colour.'
                value={s.glass.amount} unit='%' min={0} max={100}
                onChange={(v) => setGlass({ amount: v })}
              />
              <Row
                label='Blur'
                value={s.glass.blur} min={0} max={40}
                onChange={(v) => setGlass({ blur: v })}
              />
            </>
          ) : null}

          <FormControl size='small' fullWidth>
            <InputLabel>Button style</InputLabel>
            <Select
              label='Button style'
              value={s.buttonStyle}
              onChange={(e) => set({ buttonStyle: e.target.value })}
            >
              <MenuItem value='solid'>Solid</MenuItem>
              <MenuItem value='outline'>Outlined</MenuItem>
              <MenuItem value='ghost'>Tinted</MenuItem>
              <MenuItem value='pill'>Pill</MenuItem>
              <MenuItem value='square'>Square</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size='small'
            value={[...(s.border ? ['border'] : []), ...(s.shadow ? ['shadow'] : [])]}
            onChange={(_e, vals) => set({ border: vals.includes('border'), shadow: vals.includes('shadow') })}
          >
            <ToggleButton value='border'>Hairline under</ToggleButton>
            <ToggleButton value='shadow'>Shadow</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>
    </Stack>
  );
}
